import { useReportQueriesApi } from "./infrastructure/reportQueries.api";
import {
  generateReportQueriesCsv,
  generateReportQueriesHeaders,
  generateReportQueriesRequest,
  useReportQueriesFilters,
} from "./application/reportQueries.service";
import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { currentDate } from "../../lib/dateUtils";
import CrudList, { defaultListMessages } from "../shared/crud/list";
import { reportQueriesMessages } from "./messages";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { ReportQueriesAPIPromiseClient } from "../../gen/witbooking/backoffice/reportqueries/v1/reportqueries_api_grpc_web_pb";
import { useLocaleProvider } from "../../components/locale-provider";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZES = [10, 25, 50, 100, 500];

export function ReportQueriesBak() {
  const { currentLocale } = useLocaleProvider();
  const grpcClient = useGrpcClient(ReportQueriesAPIPromiseClient);
  const getReportQueries = usePropertyGrpcCall(grpcClient, grpcClient.getReportQueries);
  const { getQueriesReport } = useReportQueriesApi(getReportQueries);
  const filters = useReportQueriesFilters();
  const [totalResults, setTotalResults] = useState<number>(0);

  const headers = useMemo(() => generateReportQueriesHeaders(), []);

  const fetchQueriesReport = useCallback(
    async ({ currentPage, filterValues, pageSize, setTotalRowsCount }) => {
      const reportQueriesRequest = generateReportQueriesRequest({
        currentPage,
        filterValues,
        pageSize,
      });
      const response = await getQueriesReport(reportQueriesRequest);
      setTotalResults(response.totalResults);
      if (typeof setTotalRowsCount === "function") {
        setTotalRowsCount(response.totalResults);
      }
      return response.report;
    },
    [getQueriesReport]
  );

  const onExport = useCallback(
    async ({ currentPage, filterValues, pageSize }) => {
      if (!totalResults || !currentLocale) {
        return;
      }

      const results = await fetchQueriesReport({
        currentPage: 0,
        filterValues,
        pageSize: totalResults,
        setTotalRowsCount: undefined,
      });
      if (!results) {
        return;
      }

      const link = document.createElement("a");
      link.href = generateReportQueriesCsv(results, currentLocale);
      link.download = `queries_report_${format(currentDate(), "yyyyMMdd")}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [fetchQueriesReport, totalResults, currentLocale]
  );

  return (
    <CrudList
      messages={{
        ...defaultListMessages,
        entityName: reportQueriesMessages.title,
        tableHeaders: reportQueriesMessages,
      }}
      columns={headers}
      onGetAll={fetchQueriesReport}
      onExport={onExport}
      paging
      pageSizes={PAGE_SIZES}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      filters={filters}
      managerName={"queriesReport"}
    />
  );
}
